import { briefProgress, monotonic, overallProgress, STAGE_MILESTONES } from './buildProgress.js';
import type {
  BriefTask,
  ProposalBuildActivity,
  ProposalBuildPage,
  ProposalBuildPageStatus,
  ProposalBuildPhase,
  ProposalBuildStage,
  ProposalBuildStageState,
  ProposalBuildState,
  ProposalBuildStatus,
  ReviewItem,
} from './api.js';

const STAGE_ORDER: ProposalBuildStage[] = ['strategy', 'assembly', 'imagery', 'quality'];
const SIM_TIMESTAMP = '2020-01-01T00:00:00.000Z';

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let index = 0; index < items.length; index += size) out.push(items.slice(index, index + size));
  return out;
}

function zeroStages(): ProposalBuildStageState[] {
  return STAGE_ORDER.map((id) => ({ id, status: 'pending' as const, progress: 0 }));
}

function completeStages(): ProposalBuildStageState[] {
  return STAGE_ORDER.map((id) => ({ id, status: 'complete' as const, progress: 1 }));
}

function activeStages(stage: ProposalBuildStage, progress: number): ProposalBuildStageState[] {
  const activeIndex = STAGE_ORDER.indexOf(stage);
  return STAGE_ORDER.map((id, index) => {
    if (index < activeIndex) return { id, status: 'complete' as const, progress: 1 };
    if (index === activeIndex) return { id, status: 'active' as const, progress };
    return { id, status: 'pending' as const, progress: 0 };
  });
}

/** Legacy `status` adapter, mirroring src/state/build.ts's phase→status mapping. */
function statusForPhase(phase: ProposalBuildPhase): ProposalBuildStatus {
  switch (phase) {
    case 'intake':
      return 'idle';
    case 'preparing_brief':
      return 'building';
    case 'awaiting_confirmation':
      return 'ready';
    case 'building':
      return 'building';
    case 'needs_review':
      return 'error';
    case 'ready':
      return 'complete';
    case 'failed':
      return 'error';
  }
}

function assemblyLabel(index: number, total: number): string {
  const threshold = Math.floor((total * 2) / 3);
  return index < threshold ? 'Assembling proposal pages' : 'Writing client-facing copy';
}

function qualityLabel(step: number, totalSteps: number): string {
  if (totalSteps <= 1) return 'Preparing the final proposal';
  const third = totalSteps / 3;
  if (step < third) return 'Checking proposal structure';
  if (step < third * 2) return 'Checking the deck against the strategy';
  return 'Preparing the final proposal';
}

/** Deterministic inspection frames using the same state contract as a live build (docs/live-build.md §4). */
export function proposalBuildSimulationTimeline(seed: ProposalBuildState): ProposalBuildState[] {
  const frames: ProposalBuildState[] = [];
  const runId = `simulation-${seed.proposalId}`;

  // Developer-only activity feed, accumulated across the whole run.
  let activities: ProposalBuildActivity[] = [];
  function activityFor(stage: ProposalBuildStage, label: string): {
    activity: { label: string; startedAt: string };
    activities: ProposalBuildActivity[];
  } {
    const last = activities.at(-1);
    if (!last || last.label !== label) {
      activities = [
        ...activities,
        { id: `sim-${activities.length}`, timestamp: SIM_TIMESTAMP, stage, label, status: 'complete' as const },
      ].slice(-49);
    }
    return { activity: { label, startedAt: SIM_TIMESTAMP }, activities };
  }

  // Overall `progress` is monotonic within each contiguous "meter" — the
  // brief-preparation meter (briefProgress) and the build meter
  // (overallProgress over stages) measure different things and legitimately
  // reset relative to one another at the awaiting_confirmation → building
  // hand-off; within EACH meter, progress only holds or advances.
  let buildProgressFloor = 0;
  function buildProgress(stages: ProposalBuildStageState[]): number {
    buildProgressFloor = monotonic(buildProgressFloor, overallProgress(stages));
    return buildProgressFloor;
  }

  function push(partial: {
    phase: ProposalBuildPhase;
    currentStage?: ProposalBuildStage;
    stages: ProposalBuildStageState[];
    progress: number;
    briefTasks: BriefTask[];
    brief: ProposalBuildState['brief'];
    pages: ProposalBuildPage[];
    reviewItems: ReviewItem[];
    activity: { label: string; startedAt: string } | null;
    activities: ProposalBuildActivity[];
    canAcceptInstructions: boolean;
    message?: string;
    completedAt?: string;
  }): void {
    frames.push({
      schemaVersion: 3,
      mode: 'simulated',
      proposalId: seed.proposalId,
      runId,
      sequence: frames.length,
      attempt: 1,
      phase: partial.phase,
      proposalVersion: seed.proposalVersion,
      compositionRevision: seed.compositionRevision,
      status: statusForPhase(partial.phase),
      currentStage: partial.currentStage,
      stages: partial.stages,
      progress: partial.progress,
      briefTasks: partial.briefTasks,
      brief: partial.brief,
      details: seed.details,
      readiness: seed.readiness,
      pages: partial.pages,
      reviewItems: partial.reviewItems,
      activity: partial.activity,
      activities: partial.activities,
      startedAt: partial.phase === 'preparing_brief' || partial.phase === 'awaiting_confirmation' ? undefined : SIM_TIMESTAMP,
      completedAt: partial.completedAt,
      canAcceptInstructions: partial.canAcceptInstructions,
      message: partial.message,
    });
  }

  // ---------------------------------------------------------------------
  // B · preparing_brief — 9 row reveals across the 3 tasks, in order.
  // ---------------------------------------------------------------------
  const targetTasks = seed.briefTasks;
  const revealOrder: Array<[number, number]> = targetTasks.flatMap((task, taskIndex) =>
    task.rows.map((_, rowIndex): [number, number] => [taskIndex, rowIndex]),
  );

  function tasksAtStep(step: number): BriefTask[] {
    return targetTasks.map((task, taskIndex) => {
      const rows = task.rows.map((row, rowIndex) => {
        const order = revealOrder.findIndex(([t, r]) => t === taskIndex && r === rowIndex);
        return order < step ? row : { ...row, value: null };
      });
      const resolvedCount = rows.filter((row) => row.value !== null).length;
      const status: BriefTask['status'] =
        resolvedCount === rows.length ? 'done' : resolvedCount > 0 ? 'working' : 'queued';
      return { ...task, rows, status };
    });
  }

  function activeBriefLabel(tasks: BriefTask[]): string {
    const active = tasks.find((task) => task.status !== 'done');
    if (!active) return 'Brief ready';
    if (active.id === 'verify_business') return 'Verifying business details';
    if (active.id === 'shape_campaign') return 'Shaping the campaign';
    return 'Preparing the price recommendation';
  }

  const sourcePages: ProposalBuildPage[] = [...seed.pages]
    .sort((a, b) => a.order - b.order)
    .map((page) => ({ ...page, status: 'planned' as ProposalBuildPageStatus, imageState: 'none' as const, reviewItemIds: undefined }));
  const imageEligible = new Set(seed.pages.filter((page) => page.imageState === 'applied').map((page) => page.id));

  for (let step = 1; step <= revealOrder.length; step += 1) {
    const tasks = tasksAtStep(step);
    const label = activeBriefLabel(tasks);
    const { activity, activities: log } = activityFor('strategy', label);
    push({
      phase: 'preparing_brief',
      stages: zeroStages(),
      progress: briefProgress(tasks),
      briefTasks: tasks,
      brief: null,
      pages: sourcePages,
      reviewItems: [],
      activity,
      activities: log,
      canAcceptInstructions: false,
      message: 'Preparing the campaign brief.',
    });
  }

  // ---------------------------------------------------------------------
  // C · awaiting_confirmation — one static frame, brief filled.
  // ---------------------------------------------------------------------
  const { activity: confirmActivity, activities: confirmLog } = activityFor('strategy', 'Brief ready');
  push({
    phase: 'awaiting_confirmation',
    stages: zeroStages(),
    progress: briefProgress(targetTasks),
    briefTasks: targetTasks,
    brief: seed.brief,
    pages: sourcePages,
    reviewItems: [],
    activity: confirmActivity,
    activities: confirmLog,
    canAcceptInstructions: true,
    // docs/fan-out.md §5c/§5d: composition (and so any planned page count) is
    // now authored at build, not intake — a real seed always carries pages
    // (seedSimulation only runs against an already-built proposal), but this
    // stays a pure function of whatever `sourcePages` it's handed, so an
    // empty seed reads sensibly instead of "a 0-page proposal."
    message:
      sourcePages.length > 0
        ? `The campaign plan is ready for a ${sourcePages.length}-page proposal.`
        : 'The campaign plan is ready for a proposal.',
  });

  // ---------------------------------------------------------------------
  // D · building — strategy (3 frames)
  // ---------------------------------------------------------------------
  const strategyMilestones: Array<[number, string]> = [
    [STAGE_MILESTONES.strategy.brief_loaded, 'Loading the approved campaign brief'],
    [STAGE_MILESTONES.strategy.strategy_authored, 'Writing strategy and tactic roles'],
    [STAGE_MILESTONES.strategy.composition_ready, 'Planning the proposal story'],
  ];
  for (const [progress, label] of strategyMilestones) {
    const { activity, activities: log } = activityFor('strategy', label);
    push({
      phase: 'building',
      currentStage: 'strategy',
      stages: activeStages('strategy', progress),
      progress: buildProgress(activeStages('strategy', progress)),
      briefTasks: targetTasks,
      brief: seed.brief,
      pages: sourcePages,
      reviewItems: [],
      activity,
      activities: log,
      canAcceptInstructions: false,
    });
  }

  // ---------------------------------------------------------------------
  // D · building — assembly (one frame per page)
  // ---------------------------------------------------------------------
  let pages = sourcePages;
  const pageCount = pages.length;
  for (let index = 0; index < pageCount; index += 1) {
    pages = pages.map((page, i) => ({
      ...page,
      status: i < index ? 'content_ready' : i === index ? 'assembling' : 'planned',
    }));
    const label = assemblyLabel(index, pageCount);
    const progress =
      pageCount <= 1
        ? STAGE_MILESTONES.assembly.copy_written
        : STAGE_MILESTONES.assembly.build_started +
          (STAGE_MILESTONES.assembly.copy_written - STAGE_MILESTONES.assembly.build_started) *
            (index / (pageCount - 1));
    const { activity, activities: log } = activityFor('assembly', label);
    push({
      phase: 'building',
      currentStage: 'assembly',
      stages: activeStages('assembly', progress),
      progress: buildProgress(activeStages('assembly', progress)),
      briefTasks: targetTasks,
      brief: seed.brief,
      pages,
      reviewItems: [],
      activity,
      activities: log,
      canAcceptInstructions: false,
    });
  }
  // Settle the final page (silently — folded into the assembly → imagery hand-off,
  // not its own visible frame) so imagery starts from an all-content_ready grid.
  pages = pages.map((page) => (page.status === 'assembling' ? { ...page, status: 'content_ready' } : page));

  // ---------------------------------------------------------------------
  // D · building — imagery (3 frames)
  // ---------------------------------------------------------------------
  const imageryFrames: Array<[number, string, 'pending' | 'applied']> = [
    [STAGE_MILESTONES.imagery.search_started, 'Finding campaign imagery', 'pending'],
    [STAGE_MILESTONES.imagery.assets_selected, 'Finding campaign imagery', 'pending'],
    [STAGE_MILESTONES.imagery.images_applied, 'Applying proposal imagery', 'applied'],
  ];
  for (const [progress, label, imageState] of imageryFrames) {
    pages = pages.map((page) =>
      imageEligible.has(page.id)
        ? { ...page, status: imageState === 'applied' ? 'content_ready' : 'image_pending', imageState }
        : page,
    );
    const { activity, activities: log } = activityFor('imagery', label);
    push({
      phase: 'building',
      currentStage: 'imagery',
      stages: activeStages('imagery', progress),
      progress: buildProgress(activeStages('imagery', progress)),
      briefTasks: targetTasks,
      brief: seed.brief,
      pages,
      reviewItems: [],
      activity,
      activities: log,
      canAcceptInstructions: false,
    });
  }

  // ---------------------------------------------------------------------
  // D · building — quality (checking → ready, 3 pages at a time)
  // ---------------------------------------------------------------------
  const batches = chunk(pages.map((page) => page.id), 3);
  const totalQualitySteps = Math.max(batches.length * 2, 1);
  let qualityStep = 0;
  for (const batch of batches) {
    const batchIds = new Set(batch);
    for (const nextStatus of ['checking', 'ready'] as const) {
      pages = pages.map((page) => (batchIds.has(page.id) ? { ...page, status: nextStatus } : page));
      const progress =
        STAGE_MILESTONES.quality.document_audit +
        (STAGE_MILESTONES.quality.proposal_generated - STAGE_MILESTONES.quality.document_audit) *
          (qualityStep / Math.max(totalQualitySteps - 1, 1));
      const label = qualityLabel(qualityStep, totalQualitySteps);
      const { activity, activities: log } = activityFor('quality', label);
      push({
        phase: 'building',
        currentStage: 'quality',
        stages: activeStages('quality', progress),
        progress: buildProgress(activeStages('quality', progress)),
        briefTasks: targetTasks,
        brief: seed.brief,
        pages,
        reviewItems: [],
        activity,
        activities: log,
        canAcceptInstructions: false,
      });
      qualityStep += 1;
    }
  }

  // ---------------------------------------------------------------------
  // Review findings land — appended from the (simulated) structured audit.
  // ---------------------------------------------------------------------
  let reviewItems: ReviewItem[] = [];
  const warningPages = pages.length >= 13 ? [pages[8]!, pages[12]!] : pages.slice(-2);
  if (warningPages.length > 0) {
    const detailPages = warningPages.map((page) => pad2(page.order)).join(' and ');
    reviewItems = [
      ...reviewItems,
      {
        id: 'review-unverified-stats',
        code: 'assumed_benchmarks',
        title: '2 pages cite unverified stats',
        detail: `Pages ${detailPages} carry assumed benchmarks`,
        pageIds: warningPages.map((page) => page.id),
        severity: 'warning',
        state: 'open',
        action: { type: 'navigate', label: 'Review' },
      },
    ];
    const warningIds = new Set(warningPages.map((page) => page.id));
    pages = pages.map((page) =>
      warningIds.has(page.id)
        ? { ...page, status: 'attention', reviewItemIds: ['review-unverified-stats'] }
        : page,
    );
  }
  {
    const progress = STAGE_MILESTONES.quality.run_completed;
    const { activity, activities: log } = activityFor('quality', 'Preparing the final proposal');
    push({
      phase: 'building',
      currentStage: 'quality',
      stages: activeStages('quality', progress),
      progress: buildProgress(activeStages('quality', progress)),
      briefTasks: targetTasks,
      brief: seed.brief,
      pages,
      reviewItems,
      activity,
      activities: log,
      canAcceptInstructions: false,
    });
  }

  // ---------------------------------------------------------------------
  // Build complete; audit warnings may remain open without blocking readiness.
  // ---------------------------------------------------------------------
  const initialPhase: ProposalBuildPhase = 'ready';
  const { activity: doneActivity, activities: doneLog } = activityFor(
    'quality',
    'Proposal ready',
  );
  push({
    phase: initialPhase,
    stages: completeStages(),
    progress: buildProgress(completeStages()),
    briefTasks: targetTasks,
    brief: seed.brief,
    pages,
    reviewItems,
    activity: doneActivity,
    activities: doneLog,
    canAcceptInstructions: true,
    completedAt: SIM_TIMESTAMP,
    message: 'Proposal ready to share.',
  });

  // ---------------------------------------------------------------------
  // Everything resolved — final frame.
  // ---------------------------------------------------------------------
  reviewItems = reviewItems.map((item) => ({ ...item, state: 'resolved' as const }));
  pages = pages.map((page) => (page.status === 'attention' ? { ...page, status: 'ready' } : page));
  const { activity: finalActivity, activities: finalLog } = activityFor('quality', 'Proposal ready');
  push({
    phase: 'ready',
    stages: completeStages(),
    progress: buildProgress(completeStages()),
    briefTasks: targetTasks,
    brief: seed.brief,
    pages,
    reviewItems,
    activity: finalActivity,
    activities: finalLog,
    canAcceptInstructions: true,
    completedAt: SIM_TIMESTAMP,
    message: 'All required checks passed.',
  });

  return frames;
}
